"use client";

import DollarIcon from "@/assets/dollar.svg";
import LinkIcon from "@/assets/link-external.svg";
import AllocationDetailsCard from "@/components/AllocationDetailsCard";
import Card from "@/components/Card";
import MainSpheronSection from "@/components/MainSpheronSection";
import ClaimClosedNotice from "@/components/ClaimClosedNotice";
import ClaimTokensModal from "@/components/Modals/ClaimTokensModal";
import NotEligibleScreen from "@/components/NotEligibleScreen/NotEligibleScreen";
import SecondaryButton from "@/components/SecondaryButton/SecondaryButton";
import TrafficOverloadPage from "@/components/TrafficOverloadPage";
import UnlockRulesCard from "@/components/UnlockRulesCard";
import UsponEarningCard from "@/components/UsponEarningCard";
import VestingDetailsCard from "@/components/VestingDetailsCard";
import { isTrafficOverload, TOKENOMICS_URL } from "@/config";
import { DEXS } from "@/config/dexes";
import { claimAirdrop, getAirdropDetails, UserInfo } from "@/services/airdrop";
import { connectWallet } from "@/services/connect-wallet";
import { depositToPool } from "@/services/staking";
import { claimVested, VestingInfo } from "@/services/vesting";
import {
  ClaimWindows,
  formatClaimDeadline,
  getClaimWindows,
  OPEN_CLAIM_WINDOW,
} from "@/services/claim-window";
import {
  CLAIM_PERIOD_ENDED_ERROR,
  ClaimResult,
  DEFAULT_CLAIM_ERROR,
  getClaimErrorMessage,
} from "@/utils/contract-errors";
import { termsMessage } from "@/utils/terms-message";
import { ethers, formatEther } from "ethers";
import { useCallback, useEffect, useState } from "react";
import {
  useActiveAccount,
  useActiveWallet,
  useConnectModal,
  useDisconnect,
} from "thirdweb/react";
import { signMessage } from "thirdweb/utils";
import EthereumIcon from "../../public/ethereum.svg";
import { chain, thirdWebClient, wallets } from "./libs/thirdweb";
import ExploreSpheron from "@/components/ExploreSpheron";

export default function Page() {
  const [isFetchingAirdropDetails, setIsFetchingAirdropDetails] =
    useState(false);
  const [isEligible, setIsEligible] = useState<boolean>();
  const [allocatedSpon, setAllocatableSpon] = useState<number>(0);
  const [vestedSpon, setVestedSpon] = useState<number>(0);
  const [userShares, setUserShares] = useState<number>(0);
  const [proof, setProof] = useState<`0x${string}`[] | null>(null);
  const [vestedProof, setVestedProof] = useState<`0x${string}`[] | null>(null);
  const [claimInfo, setClaimInfo] = useState<UserInfo | null>(null);
  const [vestingInfo, setVestingInfo] = useState<VestingInfo | null>(null);
  const [airdropPercentage, setAirdropPercentage] = useState<number | null>(
    null
  );
  const [showModal, setShowModal] = useState<{
    show: boolean;
    isStaking?: boolean;
    vestedClaim?: boolean;
  }>({ show: false });
  const [claimWindows, setClaimWindows] = useState<ClaimWindows>({
    airdrop: OPEN_CLAIM_WINDOW,
    vesting: OPEN_CLAIM_WINDOW,
  });

  const activeAccount = useActiveAccount();
  const { disconnect } = useDisconnect();
  const activeWallet = useActiveWallet();
  const { connect, isConnecting } = useConnectModal();

  const fetchAirdropInfo = useCallback(async () => {
    setIsFetchingAirdropDetails(true);

    if (activeAccount?.address) {
      try {
        const address = activeAccount.address.toLowerCase();

        let signature = localStorage.getItem(`sig-${address}`);
        if (!signature) {
          signature = await signMessage({
            message: termsMessage,
            account: activeAccount,
          });
          const connectWalletResponse = await connectWallet({
            address,
            signature,
          });
          console.log("Connected Wallet:", connectWalletResponse);
          // setConnectedWallet(connectWalletResponse.wallet);
          localStorage.setItem(`sig-${address}`, signature);
        }
        const airdropDetails = await getAirdropDetails(
          activeAccount.address,
          signature
        );

        if (airdropDetails.success) {
          setIsEligible(true);
          setAllocatableSpon(Number(airdropDetails.airdropInfo.sponAmount));
          setVestedSpon(Number(airdropDetails.airdropInfo.sponVestedAmount));
          setUserShares(Number(airdropDetails.airdropInfo.shares));
          setProof(airdropDetails.airdropInfo.proof);
          setVestedProof(airdropDetails.airdropInfo.proofVestedSpon);
          setClaimInfo(airdropDetails.airdropInfo.claimInfo);
          setVestingInfo(airdropDetails.airdropInfo.vestedInfo);
          setAirdropPercentage(airdropDetails.airdropInfo.airdropPercentage);
        } else {
          setIsEligible(false);
          setAllocatableSpon(0);
          setVestedSpon(0);
          setUserShares(0);
          setProof(null);
          setVestedProof(null);
          setVestingInfo(null);
          setClaimInfo(null);
          setAirdropPercentage(null);
        }
      } catch (error) {
        console.log("Error occured in fetching airdrop details:", error);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((error as any)?.code === 4001) {
          if (activeWallet) {
            disconnect(activeWallet);
            localStorage.removeItem(
              `sig-${activeAccount.address.toLowerCase()}`
            );
            return;
          }
        }
        setIsEligible(undefined);
        setAllocatableSpon(0);
        setVestedSpon(0);
        setUserShares(0);
        setProof(null);
        setVestedProof(null);
        setClaimInfo(null);
        setVestingInfo(null);
        setAirdropPercentage(null);
      }
    } else {
      setIsEligible(undefined);
      setAllocatableSpon(0);
      setVestedSpon(0);
      setUserShares(0);
      setProof(null);
      setVestedProof(null);
      setVestingInfo(null);
      setClaimInfo(null);
      setAirdropPercentage(null);
    }
    setIsFetchingAirdropDetails(false);
  }, [activeAccount, activeWallet, disconnect]);

  useEffect(() => {
    fetchAirdropInfo();
  }, [fetchAirdropInfo]);

  // The claim deadlines are contract constants, so read them once instead of
  // letting users discover a closed window through a reverted transaction.
  useEffect(() => {
    getClaimWindows().then(setClaimWindows);
  }, []);

  const handleClaim = async (): Promise<ClaimResult> => {
    console.log("claiming " + userShares);
    if (claimWindows.airdrop.isClosed) {
      return { success: false, message: CLAIM_PERIOD_ENDED_ERROR };
    }
    if (activeAccount && proof) {
      console.log(activeAccount.address, proof);
      try {
        const response = await claimAirdrop({
          shares: ethers.parseEther(userShares.toString()),
          proof,
          account: activeAccount,
        });
        console.log("Claim Response:", response);
        await fetchAirdropInfo();
        return { success: true };
      } catch (error) {
        console.error(error);
        return { success: false, message: getClaimErrorMessage(error) };
      }
    }
    return { success: false, message: DEFAULT_CLAIM_ERROR };
  };

  const handleVestedClaim = async (): Promise<ClaimResult> => {
    if (claimWindows.vesting.isClosed) {
      return { success: false, message: CLAIM_PERIOD_ENDED_ERROR };
    }
    if (vestedProof && activeAccount) {
      console.log(activeAccount.address, vestedProof);
      try {
        const response = await claimVested({
          shares: ethers.parseEther(vestedSpon.toString()),
          proof: vestedProof,
          account: activeAccount,
        });
        console.log("Claim Response:", response);
        await fetchAirdropInfo();
        return { success: true };
      } catch (error) {
        console.error(error);
        return { success: false, message: getClaimErrorMessage(error) };
      }
    }
    return { success: false, message: DEFAULT_CLAIM_ERROR };
  };

  const onConnectClick = async () => {
    if (isConnecting) return;

    const wallet = await connect({
      client: thirdWebClient,
      wallets,
      size: "compact",
    });
    await wallet?.switchChain(chain);
  };

  const handleStake = async () => {
    console.log("staking " + userShares);
    if (activeAccount && proof) {
      console.log(activeAccount.address, proof);
      try {
        const txnHash = await depositToPool(
          BigInt(Number(userShares) * 10 ** 18),
          activeAccount.address,
          activeAccount
        );
        console.log("stake hash:", txnHash);

        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }
    return false;
  };

  // If traffic overload is enabled, show the overload page
  if (isTrafficOverload) {
    return <TrafficOverloadPage />;
  }

  if (activeAccount?.address && isEligible)
    return (
      <div className="flex flex-col gap-[42px] pt-[78px] w-full">
        <h1 className="font-gilroy-bold text-[24px] md:text-[36px] lg:text-[48px] text-white text-center">
          🎉{" "}
          {!!claimInfo?.claimed
            ? "YOU'VE CLAIMED THE SPON REWARD"
            : "YOU GOT THE SPON REWARD"}
          !
        </h1>
        <div className="w-full flex gap-4 md:flex-row flex-col justify-start">
          <div className="basis-full flex flex-col gap-4">
            <AllocationDetailsCard
              walletAddress={activeAccount?.address}
              allocatedSpon={allocatedSpon}
              vestedSpon={vestedSpon}
              isClaimed={!!claimInfo?.claimed}
            />
            <UnlockRulesCard
              dayOneUnlockPerc={airdropPercentage || 0}
              // This card covers the whole reward, so it only reads as closed
              // once neither the airdrop nor the vesting claim is possible.
              isClaimClosed={
                claimWindows.airdrop.isClosed && claimWindows.vesting.isClosed
              }
              claimDeadline={Math.max(
                claimWindows.airdrop.deadline ?? 0,
                claimWindows.vesting.deadline ?? 0
              )}
            />
            <div className="w-full bg-white/5 border border-[#2C2C30] bg-[#141316] p-4 flex flex-col md:flex-row gap-6 md:gap-3 items-center">
              <div className="text-white font-jetbrans-mono text-sm">
                Take a deep dive into our tokenomics to understand how our token
                plays a key role in powering the Spheron Network.
              </div>
              <SecondaryButton
                className="max-w-full md:max-w-[160px]"
                onClick={() => {
                  window.open(TOKENOMICS_URL, "_blank");
                }}
                variant="light"
              >
                Check it out
              </SecondaryButton>
            </div>
            {!claimInfo?.claimed && allocatedSpon > 0 && (
              <Card className="gap-3 py-4 px-4 md:!px-8 flex-col !items-start justify-start">
                <div className="flex flex-col gap-1">
                  <div className="font-gilroy-regular text-lg uppercase">
                    {!claimInfo?.claimed
                      ? "Claim your SPON Reward"
                      : "You have claimed your SPON Reward"}
                  </div>
                  {!claimWindows.airdrop.isClosed && (
                    <span className="text-sm text-[#9D9D9D]">
                      You should claim all the tokens before{" "}
                      <span className="text-white font-semibold">
                        {formatClaimDeadline(claimWindows.airdrop.deadline) ??
                          "29 May 2026"}
                      </span>
                    </span>
                  )}
                </div>
                {claimWindows.airdrop.isClosed && (
                  <ClaimClosedNotice deadline={claimWindows.airdrop.deadline} />
                )}
                <div className="w-full flex flex-col md:flex-row gap-4 items-center">
                  <div className="w-full font-semibold text-2xl">
                    {Number(allocatedSpon.toFixed(2))} SPON
                  </div>
                  <div className="w-full flex flex-row gap-4">
                    <SecondaryButton
                      disabled={claimWindows.airdrop.isClosed}
                      onClick={() => {
                        setShowModal({ show: true, isStaking: false });
                      }}
                      variant="light"
                      className="md:max-w-[250px] md:ml-auto"
                    >
                      {claimWindows.airdrop.isClosed
                        ? "Claim Period Ended"
                        : "Claim SPON"}
                    </SecondaryButton>
                    {/* <SecondaryButton
                  onClick={() => {
                    setShowModal({ show: true, isStaking: true });
                  }}
                  variant="light"
                >
                  Claim and Stake
                </SecondaryButton> */}
                  </div>
                </div>
              </Card>
            )}
            {vestedSpon > 0 && (
              <VestingDetailsCard
                isClaimClosed={claimWindows.vesting.isClosed}
                claimDeadline={claimWindows.vesting.deadline}
                airdropPercentage={airdropPercentage || 0}
                claimedSpon={
                  vestingInfo?.claimedShares
                    ? Math.max(0, Number(vestingInfo?.claimedShares))
                    : 0
                }
                unlockedSpon={
                  vestingInfo
                    ? Math.max(0, Number(vestingInfo?.claimableShares))
                    : 0
                }
                airdropSponAmount={allocatedSpon || 0}
                remainingSpon={
                  vestingInfo
                    ? Math.max(0, Number(vestingInfo?.remainingShares))
                    : 0
                }
                showModal={() =>
                  setShowModal({ show: true, vestedClaim: true })
                }
              />
            )}

            <Card
              id="buy-spon"
              className="w-full self-start flex flex-col gap-4 p-4 !px-4 !items-start"
            >
              <h2 className="text-xl md:font-semibold flex items-center gap-2">
                <DollarIcon className="h-6 w-6" /> BUY SPON
              </h2>
              <div className="flex gap-4 w-full flex-col md:flex-row">
                {DEXS.map((item) => {
                  return (
                    <a
                      key={item.id}
                      href={item.link}
                      className="basis-[24%]"
                      target="_blank"
                    >
                      <Card className="text-white hover:border-[#4C4C40] flex !items-center gap-2 !px-4 !py-2 !bg-black">
                        {item.logo}
                        {item.name}
                        <LinkIcon className="ml-auto w-4 h-4" />
                      </Card>
                    </a>
                  );
                })}
              </div>
            </Card>
            <UsponEarningCard />
            <ExploreSpheron isEligible />
          </div>
        </div>
        <ClaimTokensModal
          close={() => setShowModal({ show: false })}
          isOpen={showModal.show}
          allocatedSpon={
            showModal.vestedClaim
              ? Number(formatEther(BigInt(vestingInfo?.claimableShares || "0")))
              : allocatedSpon
          }
          onClaim={handleClaim}
          onVestedClaim={handleVestedClaim}
          onStake={handleStake}
          isStaking={!!showModal.isStaking}
          isVestedClaim={!!showModal.vestedClaim}
        />
      </div>
    );

  if (
    typeof isEligible !== "undefined" &&
    !isEligible &&
    activeAccount?.address
  ) {
    return <NotEligibleScreen />;
  }

  return (
    <>
      <div className="flex flex-col items-center gap-y-[72px] mt-[65px]">
        <div className="flex flex-col items-center gap-y-8 lg:max-w-[734px] w-[95%] text-center">
          <div className="font-gilroy-bold text-5xl uppercase text-white">
            Claim SPON Reward
          </div>
          <div className="flex flex-col gap-y-4 font-inter">
            <p className="text-[#9D9D9D] leading-6">
              Enter the era of programmable compute,{" "}
              <span className="font-bold text-white">
                powering the next generation of AI agents.
              </span>{" "}
              With <span className="font-bold text-white">SPON</span>, we're
              redefining the future of decentralized compute network & AI Agent
              Economy.
            </p>
            <p className="text-white leading-8">
              Join us in shaping and owning a part of the DePIN GPU
              supercomputing network.
            </p>
          </div>
        </div>
        <MainSpheronSection
          firstText={
            <>
              If you are an <span className="font-bold text-white">Fizzer</span>{" "}
              or{" "}
              <span className="font-bold text-white">
                Users of Spheron Network
              </span>{" "}
              and was a part of our partner network.
            </>
          }
          secondText={<>Connect your wallet to check your eligibility</>}
          ctaText={
            <>
              <EthereumIcon className="w-5 h-5" /> CONNECT WALLET
            </>
          }
          onCtaClick={() => {
            onConnectClick();
          }}
          isLoading={isFetchingAirdropDetails}
        />
      </div>
    </>
  );
}
